// Globals
import { TableColumnsType } from 'antd';
import { TFunction } from 'i18next';

// Models
import { ConversationStatus, IConversation } from '@/models/conversation.model';

// Modules
import { ChannelsSelect } from '@/modules/Channels';

// Components
import { Input, Select, TSelectOptions } from '@/ui';
import {
  StyledActionLinkButton,
  StyledChannelTag,
  StyledCountBadge,
  StyledDate,
  StyledId,
  StyledSearchWrapper,
  StyledStatusPill,
} from '../components';

// Helpers
import { ROUTES } from '@/constants/routes';
import { DateFormats } from '@/constants/dates';
import { getShortId, renderDate } from '@/utils';
import { TConversationFilters } from './parseConversationFilters';
import { getConversationStatusLabel } from './getConversationStatusLabel';

type TGetConversationColumnsParams = {
  t: TFunction;
  filters?: TConversationFilters;
  onFilterChange: <K extends keyof TConversationFilters>(field: K, value: TConversationFilters[K]) => void;
  statusOptions?: TSelectOptions<ConversationStatus>;
};

export const getConversationColumns = ({
  t,
  filters,
  onFilterChange,
  statusOptions,
}: TGetConversationColumnsParams): TableColumnsType<IConversation> => [
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('column_id')}</span>
        <Input
          defaultValue={filters?.conversationId}
          onChange={(event) => onFilterChange('conversationId', event.target.value)}
          size="small"
          placeholder={t('placeholder_search_conversation')}
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'conversationId',
    key: 'conversationId',
    width: 140,
    render: (conversationId: string) => <StyledId>{getShortId(conversationId)}</StyledId>,
  },
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('column_channel')}</span>
        <ChannelsSelect
          value={filters?.channelId}
          onChange={(channelId) => onFilterChange('channelId', channelId)}
          size="small"
          placeholder={t('column_channel')}
          allowClear
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'channelSlug',
    key: 'channelSlug',
    width: 180,
    render: (channelSlug: string) => <StyledChannelTag>{channelSlug}</StyledChannelTag>,
  },
  {
    title: t('column_start'),
    dataIndex: 'startedAt',
    key: 'startedAt',
    width: 180,
    render: (startedAt: string) => <StyledDate>{renderDate(startedAt, DateFormats.DATE_WITH_TIME)}</StyledDate>,
  },
  {
    title: t('column_end'),
    dataIndex: 'endedAt',
    key: 'endedAt',
    width: 180,
    render: (endedAt: string) => <StyledDate>{renderDate(endedAt, DateFormats.DATE_WITH_TIME)}</StyledDate>,
  },
  {
    title: t('menu_suggested_faq'),
    dataIndex: 'suggestionCount',
    key: 'suggestionCount',
    width: 100,
    render: (suggestionCount?: number) => <StyledCountBadge>{suggestionCount ?? 0}</StyledCountBadge>,
  },
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('label_status')}</span>
        <Select
          options={statusOptions}
          value={filters?.state}
          onChange={(value) => onFilterChange('state', value)}
          size="small"
          placeholder={t('label_status')}
          allowClear
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'state',
    key: 'state',
    width: 160,
    render: (conversationStatus: ConversationStatus) => (
      <StyledStatusPill $status={conversationStatus}>
        {getConversationStatusLabel(conversationStatus, t)}
      </StyledStatusPill>
    ),
  },
  {
    title: t('column_action'),
    key: 'action',
    width: 80,
    render: (_, record) => (
      <StyledActionLinkButton to={`${ROUTES.CONVERSATIONS}/${record.conversationId}`}>
        {t('view_btn')}
      </StyledActionLinkButton>
    ),
  },
];
